import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  LearningVocabulary,
  LearningSummary,
  LearningStatusFilter,
} from '../types/learning.types';
import { learningProgressService } from '../services/learning-progress.service';
import { Flashcard } from '../components/learning/Flashcard';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorMessage } from '../components/common/ErrorMessage';

const LearnIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const FilterEmptyIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>
);

export function LearnPage(): React.ReactElement {
  const navigate = useNavigate();

  const [vocabularies, setVocabularies] = useState<LearningVocabulary[]>([]);
  const [summary, setSummary] = useState<LearningSummary>({
    totalCount: 0,
    learnedCount: 0,
    unlearnedCount: 0,
  });
  const [statusFilter, setStatusFilter] = useState<LearningStatusFilter>('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDeck = useCallback(async (filter: LearningStatusFilter) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await learningProgressService.getLearningVocabularies({
        status: filter,
        limit: 100,
      });
      setVocabularies(res.items);
      setSummary(res.summary);
      setCurrentIndex(0);
      setIsFlipped(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flashcards.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeck(statusFilter);
  }, [statusFilter, fetchDeck]);

  const handleNext = useCallback(() => {
    if (currentIndex < vocabularies.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsFlipped(false);
    }
  }, [currentIndex, vocabularies.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsFlipped(false);
    }
  }, [currentIndex]);

  const handleFlip = useCallback(() => {
    setIsFlipped((prev) => !prev);
  }, []);

  const handleToggleLearned = async () => {
    const currentVocab = vocabularies[currentIndex];
    if (!currentVocab || isUpdating) return;

    const nextLearned = !currentVocab.learning.isLearned;
    setIsUpdating(true);
    try {
      const updated = await learningProgressService.updateProgress(currentVocab.id, nextLearned);

      // Update current card in list
      setVocabularies((prev) =>
        prev.map((item, idx) =>
          idx === currentIndex
            ? {
                ...item,
                learning: {
                  isLearned: updated.isLearned,
                  learnedAt: updated.learnedAt,
                },
              }
            : item,
        ),
      );

      // Update summary counts
      setSummary((prev) => ({
        ...prev,
        learnedCount: nextLearned ? prev.learnedCount + 1 : Math.max(0, prev.learnedCount - 1),
        unlearnedCount: nextLearned
          ? Math.max(0, prev.unlearnedCount - 1)
          : prev.unlearnedCount + 1,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update learned status.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if an input is focused
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        handleFlip();
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFlip, handleNext, handlePrev]);

  const currentVocab = vocabularies[currentIndex];
  const percentLearned =
    summary.totalCount > 0 ? Math.round((summary.learnedCount / summary.totalCount) * 100) : 0;

  return (
    <div className="space-y-6 animate-slide-in pb-12">
      {/* ─── HEADER & STATS ──────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Flashcards</h1>
          <p className="text-sm text-slate-400 mt-1">
            Study your vocabulary deck with interactive flip cards and track what you've learned.
          </p>
        </div>

        {/* Global Progress pill */}
        {summary.totalCount > 0 && (
          <div
            id="learning-stats-widget"
            className="flex items-center gap-3 bg-surface-800/80 border border-white/10 rounded-2xl px-4 py-2.5 backdrop-blur-sm self-start sm:self-auto"
          >
            <div className="flex flex-col items-end">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Mastery Progress
              </span>
              <span className="text-sm font-bold text-white">
                {summary.learnedCount} / {summary.totalCount} words ({percentLearned}%)
              </span>
            </div>
            <div className="w-16 bg-surface-700 h-2 rounded-full overflow-hidden">
              <div
                className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${percentLearned}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => fetchDeck(statusFilter)}
        />
      )}

      {/* ─── FILTER TABS ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div id="deck-filter-tabs" className="inline-flex rounded-xl bg-surface-800/80 p-1 border border-white/10">
          <button
            type="button"
            id="filter-all-btn"
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              statusFilter === 'all'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            All Cards ({summary.totalCount})
          </button>
          <button
            type="button"
            id="filter-unlearned-btn"
            onClick={() => setStatusFilter('unlearned')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              statusFilter === 'unlearned'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            To Learn ({summary.unlearnedCount})
          </button>
          <button
            type="button"
            id="filter-learned-btn"
            onClick={() => setStatusFilter('learned')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              statusFilter === 'learned'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Learned ({summary.learnedCount})
          </button>
        </div>

        {/* Keyboard shortcut hint */}
        <div className="hidden md:flex items-center gap-3 text-xs text-slate-500 font-mono">
          <span><kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-white/10 text-slate-300">Space</kbd> Flip</span>
          <span><kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-white/10 text-slate-300">←</kbd> Prev</span>
          <span><kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-white/10 text-slate-300">→</kbd> Next</span>
        </div>
      </div>

      {/* ─── MAIN CONTENT AREA ───────────────────────────────── */}
      {isLoading ? (
        <div className="py-24 flex justify-center">
          <LoadingSpinner size="lg" label="Loading flashcards..." />
        </div>
      ) : vocabularies.length === 0 ? (
        summary.totalCount === 0 ? (
          <EmptyState
            title="No Vocabulary Yet"
            description="You don't have any vocabulary items to study. Add some words to your vocabulary list first."
            icon={<LearnIcon />}
            action={{
              label: 'Go to Vocabulary',
              onClick: () => navigate('/vocabulary'),
            }}
          />
        ) : (
          <EmptyState
            title={`No ${statusFilter} words`}
            description={
              statusFilter === 'learned'
                ? "You haven't marked any words as learned yet. Go through your deck and mark words you master!"
                : "Great job! You've learned all your vocabulary words. You can review them anytime."
            }
            icon={<FilterEmptyIcon />}
            action={{
              label: 'View All Cards',
              onClick: () => setStatusFilter('all'),
            }}
          />
        )
      ) : (
        <div className="space-y-6">
          {/* Card counter */}
          <div className="flex items-center justify-between max-w-2xl mx-auto px-2">
            <span id="card-counter" className="text-xs font-semibold tracking-wider uppercase text-slate-400">
              Card {currentIndex + 1} of {vocabularies.length}
            </span>
            <span className="text-xs text-slate-500 font-medium">
              {isFlipped ? 'Back (Definition)' : 'Front (Word)'}
            </span>
          </div>

          {/* Flashcard Component */}
          {currentVocab && (
            <Flashcard
              vocabulary={currentVocab}
              isFlipped={isFlipped}
              onFlip={handleFlip}
            />
          )}

          {/* Controls Bar: Prev, Mark Learned, Next */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-2xl mx-auto pt-2">
            {/* Previous button */}
            <Button
              id="prev-card-btn"
              variant="secondary"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="w-full sm:w-auto min-w-[110px]"
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              }
            >
              Previous
            </Button>

            {/* Toggle Learned Button */}
            {currentVocab && (
              <Button
                id="toggle-learned-btn"
                variant={currentVocab.learning.isLearned ? 'secondary' : 'primary'}
                onClick={handleToggleLearned}
                loading={isUpdating}
                className={`w-full sm:w-auto font-semibold px-6 ${
                  currentVocab.learning.isLearned
                    ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30'
                }`}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                }
              >
                {currentVocab.learning.isLearned ? '✓ Marked as Learned' : 'Mark as Learned'}
              </Button>
            )}

            {/* Next button */}
            <Button
              id="next-card-btn"
              variant="secondary"
              onClick={handleNext}
              disabled={currentIndex === vocabularies.length - 1}
              className="w-full sm:w-auto min-w-[110px]"
              rightIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
